import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchGalleryImages = createAsyncThunk(
  "gallery/fetchGalleryImages",
  async ({ page = 1, limit = 20, category = "" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(category && { category }),
      });

      const response = await api.get(`/gallery?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch gallery images",
      );
    }
  },
);

export const fetchGalleryCategories = createAsyncThunk(
  "gallery/fetchGalleryCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/gallery/categories");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories",
      );
    }
  },
);

export const uploadGalleryImage = createAsyncThunk(
  "gallery/uploadGalleryImage",
  async (imageData, { rejectWithValue }) => {
    try {
      const response = await api.post("/gallery/", imageData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload image",
      );
    }
  },
);

export const updateGalleryImage = createAsyncThunk(
  "gallery/updateGalleryImage",
  async ({ id, imageData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/gallery/${id}`, imageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update image",
      );
    }
  },
);

export const deleteGalleryImage = createAsyncThunk(
  "gallery/deleteGalleryImage",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/gallery/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete image",
      );
    }
  },
);

export const toggleImageStatus = createAsyncThunk(
  "gallery/toggleImageStatus",
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/gallery/${id}`, { isActive });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update image status",
      );
    }
  },
);

export const bulkUploadGalleryImages = createAsyncThunk(
  "gallery/bulkUploadGalleryImages",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post("/gallery/bulk-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload images",
      );
    }
  },
);

// Initial state
const initialState = {
  images: [],
  categories: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalImages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    category: "",
  },
  uploadProgress: 0,
};

// Slice
const gallerySlice = createSlice({
  name: "gallery",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch gallery images
      .addCase(fetchGalleryImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGalleryImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload.data.images;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchGalleryImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch categories
      .addCase(fetchGalleryCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGalleryCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.categories;
      })
      .addCase(fetchGalleryCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upload image
      .addCase(uploadGalleryImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadGalleryImage.fulfilled, (state, action) => {
        state.loading = false;
        state.images.unshift(action.payload.data.image);
        state.pagination.totalImages += 1;
        state.uploadProgress = 0;
      })
      .addCase(uploadGalleryImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadProgress = 0;
      })

      // Update image
      .addCase(updateGalleryImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGalleryImage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.images.findIndex(
          (img) => img._id === action.payload.data.image._id,
        );
        if (index !== -1) {
          state.images[index] = action.payload.data.image;
        }
      })
      .addCase(updateGalleryImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete image
      .addCase(deleteGalleryImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGalleryImage.fulfilled, (state, action) => {
        state.loading = false;
        state.images = state.images.filter((img) => img._id !== action.payload);
        state.pagination.totalImages -= 1;
      })
      .addCase(deleteGalleryImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle image status
      .addCase(toggleImageStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleImageStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data.image;
        const index = state.images.findIndex((img) => img._id === updated._id);
        if (index !== -1) {
          state.images[index] = updated;
        }
      })
      .addCase(toggleImageStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk upload
      .addCase(bulkUploadGalleryImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUploadGalleryImages.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally refresh images list
        state.uploadProgress = 0;
      })
      .addCase(bulkUploadGalleryImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadProgress = 0;
      });
  },
});

export const {
  clearError,
  setFilters,
  setUploadProgress,
  resetUploadProgress,
} = gallerySlice.actions;
export default gallerySlice.reducer;
